import { Box, Grid, Popover, Typography } from '@atgfr8/atg-ui-tailwind';
import {
  LtlQuoteViewModel,
  LtlTerminalInfoViewModel,
} from '@store/orders/types';
import { InformationOutlineIcon } from '@atgfr8/icons';
import { ConfigManager } from '@utils/ConfigManager';
import { formatAmount } from '@utils/utils';
import { createSignal, Show } from 'solid-js';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { FeatureFlags } from '@constants/featureFlags';

import { ViewTerminalPopover } from '../../ViewTerminalPopover';
import { quoteDetailsRowRenderClasses as cls } from '../classes';

export const QuoteCarrierDetailsLegacy = (props: {
  quote: LtlQuoteViewModel;
  testMode: boolean;
}) => {
  const handleTerminalOpen = () => setTerminalModalOpen(true);
  const handleTerminalClose = () => setTerminalModalOpen(false);
  const [terminalModalOpen, setTerminalModalOpen] = createSignal(false);
  const isCarrierQuoteExpirationEnabled = isFeatureFlagEnabled(
    FeatureFlags.LtlCarrierQuoteExpirationDisplay,
  )();
  const showCarrierQuoteExpiration = () =>
    ['PricingDynamic', 'PricingVolume', 'Dynamic', 'Volume'].includes(
      props.quote.pricingRequestType ?? '',
    ) &&
    props.quote.expirationDate !== null &&
    isCarrierQuoteExpirationEnabled;
  const getFormattedQuoteExpiration = (): string => {
    const expirationDate: string | null | undefined =
      props.quote.expirationDate;
    if (!expirationDate) {
      return 'Not available';
    }
    return new Date(expirationDate).toLocaleDateString();
  };
  return (
    <Box
      class={`${cls.fullHeightColumn} w-full`}
      style={{ display: 'flex', 'flex-direction': 'column', height: '100%' }}
      p={2}
    >
      <Grid container spacing={2} alignItems="stretch">
        {/* First Column - Carrier Info and Logo */}
        <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
          <Typography
            variant="body1"
            class="font-medium! text-wrap! wrap-break-word!"
            data-testid="quoteCarrierDetails-carrierName-quote-{props.quote.id}"
          >
            {props.quote.carrierName}
          </Typography>

          <Typography
            variant="body1"
            class={`${cls.labelText} text-wrap! wrap-break-word!`}
            sxProps={{ marginTop: '8px' }}
          >
            Carrier Quote ID
          </Typography>
          <Typography
            variant="body1"
            class={`${cls.infoText} text-wrap! wrap-break-word!`}
          >
            {props.quote.carrierQuoteId}
          </Typography>
          <Box
            class={`${cls.logoBox} flex justify-center sm:justify-start`}
            mt={3}
          >
            <img
              src={`${ConfigManager.apiUrl}/api/carrier/logo/${props.quote.carrierId}`}
              alt="carrier logo"
              class={cls.carrierLogo}
            />
          </Box>
        </Grid>

        {/* Second Column (Transit Days, Delivery Date, Service Type) */}
        <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
          <Typography
            variant="body1"
            class={`${cls.labelText} text-wrap! wrap-break-word!`}
          >
            Est. Transit Days
          </Typography>
          <Typography
            variant="body1"
            class={`${cls.infoText} text-wrap! wrap-break-word!`}
          >
            {props.quote.transitDays}
          </Typography>

          <Typography
            variant="body1"
            class={`${cls.labelText} text-wrap! wrap-break-word!`}
            sxProps={{ marginTop: '16px' }}
          >
            Est. Delivery Date
          </Typography>
          <Typography
            variant="body1"
            class={`${cls.infoText} text-wrap! wrap-break-word!`}
          >
            {props.quote.estimatedDeliveryDate != null
              ? new Date(props.quote.estimatedDeliveryDate).toLocaleDateString()
              : '--'}
          </Typography>

          <Typography
            variant="body1"
            class={`${cls.labelText} text-wrap! wrap-break-word!`}
            sxProps={{ marginTop: '16px' }}
          >
            Service Type
          </Typography>
          <Typography
            variant="body1"
            class={`${cls.infoText} text-wrap! wrap-break-word!`}
          >
            {props.quote.serviceTypeDisplayName}
          </Typography>

          <Show when={showCarrierQuoteExpiration()}>
            <Typography
              variant="body1"
              class={`${cls.labelText} text-wrap! wrap-break-word! mt-4!`}
            >
              Quote Expires
            </Typography>
            <Typography
              variant="body1"
              class={`${cls.infoText} text-wrap! wrap-break-word!`}
              data-testid="quoteCarrierDetails-carrierQuoteExpiration"
            >
              {getFormattedQuoteExpiration()}
            </Typography>
          </Show>
        </Grid>

        {/* Third Column (Terminal Info, Movement Type, Carrier Liability) */}
        <Grid item size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
          {!props.testMode && (
            <Box mb={2}>
              <button
                class="flex items-center text-[#307eac] text-wrap! wrap-break-word!"
                data-testid="quoteCarrierDetails-terminalInfo-quote-{props.quote.id}"
                onClick={handleTerminalOpen}
              >
                <InformationOutlineIcon class="mr-1 shrink-0" />
                <span class="text-wrap! wrap-break-word!">Terminal Info</span>
              </button>

              <Popover
                open={terminalModalOpen()}
                onClose={handleTerminalClose}
                anchorReference={'none'}
                classes={{ root: cls.terminalPopoverRoot }}
                dataTestId={`quote-carrier-details-terminal-popover-${props.quote.id}`}
              >
                <ViewTerminalPopover
                  terminalInfo={
                    props.quote.terminalInfo as LtlTerminalInfoViewModel
                  }
                  quoteId={String(props.quote.id)}
                />
              </Popover>
            </Box>
          )}

          <Typography
            variant="body1"
            class={`${cls.labelText} text-wrap! wrap-break-word!`}
            sxProps={{ marginTop: '16px' }}
          >
            Movement Type
          </Typography>
          <Typography
            variant="body1"
            class={`${cls.infoText} text-wrap! wrap-break-word!`}
          >
            {props.quote.movementType ?? '--'}
          </Typography>

          <Typography
            variant="body1"
            class={`${cls.labelText} text-wrap! wrap-break-word!`}
            sxProps={{ marginTop: '16px' }}
          >
            Max Carrier Liability
          </Typography>
          <Box class="flex flex-wrap gap-2">
            <Box class="flex items-center mr-4">
              <Typography
                variant="body1"
                component="span"
                class={`${cls.infoTextBold} mr-1 text-wrap! wrap-break-word!`}
              >
                New:
              </Typography>
              <Typography
                variant="body1"
                component="span"
                class={`${cls.infoText} text-wrap! wrap-break-word!`}
              >
                {formatAmount(props.quote.maxNewLiability)}
              </Typography>
            </Box>
            <Box class="flex items-center">
              <Typography
                variant="body1"
                component="span"
                class={`${cls.infoTextBold} mr-1 text-wrap! wrap-break-word!`}
              >
                Used:
              </Typography>
              <Typography
                variant="body1"
                component="span"
                class={`${cls.infoText} text-wrap! wrap-break-word!`}
              >
                {formatAmount(props.quote.maxUsedLiability)}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Show when={props.quote.carrierNotes?.trim()}>
        <Box class="flex items-start" mt={2}>
          <Typography
            variant="body1"
            component="span"
            class={cls.carrierNoteLabel}
            data-testid="quoteCarrierDetails-carrierNote"
          >
            Carrier Note
          </Typography>
          <Typography
            variant="body1"
            component="span"
            class={cls.carrierNoteText}
          >
            {`"${props.quote.carrierNotes}"`}
          </Typography>
        </Box>
      </Show>
    </Box>
  );
};
